/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */
import './style/invoiceFilter.scss';

import {ChangeFunction} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import React, {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ISO_DATE_FORMAT} from '../../../util/constants';

import {COUNTRIES} from '../../../util/countryUtil';

import {PeriodLabel} from '../../../util/enums';

import {CompleteInvoiceFilter, CustomerType, InvoiceStatus, InvoiceType} from '../../../util/interfaces';

import {getOrderStatusItems} from '../../../util/dashboardUtil';

import {PortalSelectItem} from '../../../util/types';

import {BluemateDialog, BluemateInput, BluematePeriodSelector} from '../../controls';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import classNames from 'classnames';

interface Props {
    onChangeFilter: (key: keyof CompleteInvoiceFilter) => ChangeFunction<string | null>;
    onChangeFilters: (values: Partial<CompleteInvoiceFilter>) => void;
    onSubmitFilters: VoidFunction;
    onResetFilters: VoidFunction;
    onClose: VoidFunction;
    filters: CompleteInvoiceFilter;
    active: boolean;
}

export default function InvoiceFilter(props: Props) {
    const {t} = useTranslation();
    const {onClose, active, filters, onChangeFilter, onChangeFilters, onResetFilters, onSubmitFilters} = props;
    // Label of the preset picked in the period selector — display-only; the
    // filter itself stores the concrete date_from/date_to values.
    const [periodPreset, setPeriodPreset] = useState<PeriodLabel | null>(null);
    const statusItems: Array<PortalSelectItem<string>> = [
        {label: t('dashboard.filter.all', 'Alle'), value: ''},
        {label: t('dashboard.filter.status.sent', 'Verzonden'), value: InvoiceStatus.SENT},
        {label: t('dashboard.filter.status.notSent', 'Niet verzonden'), value: InvoiceStatus.IMPORTED},
        {label: t('dashboard.filter.status.sending', 'Wordt verzonden'), value: InvoiceStatus.TRANSPORT},
        {label: t('dashboard.filter.status.error', 'Fout'), value: InvoiceStatus.ERROR},
        {label: t('dashboard.filter.status.warning', 'Waarschuwing'), value: InvoiceStatus.WARNING}
    ];
    const orderStatusItems = getOrderStatusItems(t('dashboard.filter.all', 'Alle'));
    const invoiceTypeItems: Array<PortalSelectItem<string>> = [
        {label: t('dashboard.filter.all', 'Alle'), value: ''},
        {label: t('dashboard.filter.invoiceType.sales', 'Verkoopfacturen'), value: InvoiceType.DEBET},
        {label: t('dashboard.filter.invoiceType.credit', 'Creditfacturen'), value: InvoiceType.CREDIT}
    ];
    const customerTypeItems: Array<PortalSelectItem<string>> = [
        {label: t('dashboard.filter.all', 'Alle'), value: CustomerType.BOTH},
        {label: t('dashboard.filter.customerType.private', 'Particulier'), value: CustomerType.PRIVATE},
        {label: t('dashboard.filter.customerType.business', 'Zakelijk'), value: CustomerType.BUSINESS}
    ];
    // ~250 entries and the dialog renders on every keystroke of the main
    // search field (it is mounted inline under the dashboard) — memoize.
    const countryItems: Array<PortalSelectItem<string>> = useMemo(
        () => [
            {label: t('dashboard.filter.all', 'Alle'), value: ''},
            ...Object.keys(COUNTRIES).map((countryCode: string) => ({
                label: COUNTRIES[countryCode],
                // Parity with the previous CountryField (labelAsValue): the
                // filter stores the country NAME, which the backend matches.
                value: COUNTRIES[countryCode]
            }))
        ],
        [t]
    );
    const dateFrom = filters.date_from ? new IDate(filters.date_from) : null;
    const dateTo = filters.date_to ? new IDate(filters.date_to) : null;

    function getLabelClassName(filterValue: string | null) {
        return filterValue ? 'bluemate-label-active' : undefined;
    }

    function fieldIsActive(filterValue: string | IDate | null | undefined): boolean {
        if (filterValue === null || filterValue === undefined) return false;
        else if (typeof filterValue === 'string' && !filterValue.length) return false;
        return true;
    }

    function onChangePeriod(range: [IDate, IDate], presetLabel: PeriodLabel | null) {
        // In a filter "Alles" means no date restriction — not the selector's
        // fallback 10-year range.
        if (presetLabel === PeriodLabel.ALL) {
            setPeriodPreset(null);
            onChangeFilters({date_from: null, date_to: null});
            return;
        }
        setPeriodPreset(presetLabel);
        onChangeFilters({
            date_from: range[0].format(ISO_DATE_FORMAT),
            date_to: range[1].format(ISO_DATE_FORMAT)
        });
    }

    useEffect(() => {
        // External resets (Clear filters, account switch) null the dates —
        // drop the remembered preset label so the control reads "Alles" again.
        if (!filters.date_from && !filters.date_to) setPeriodPreset(null);
    }, [filters.date_from, filters.date_to]);

    function renderSelect(
        key: keyof CompleteInvoiceFilter,
        label: string,
        items: Array<PortalSelectItem<string>>,
        itemsHeight?: string
    ) {
        const value = (filters[key] as string | null) ?? '';
        return (
            <PortalSelectController
                onChange={(nextValue: string | null) => onChangeFilter(key)(nextValue)}
                value={value}
                items={items}
                label={label}
                shape='rounded'
                uniformFont
                noShadow
                className={classNames('filter-select', fieldIsActive(value) && 'active-filter')}
                itemsClassName='filter-select-items'
                itemsHeight={itemsHeight}
            />
        );
    }

    function renderCustomerType() {
        const isActive = filters.customer_type !== CustomerType.BOTH;
        return (
            <PortalSelectController
                onChange={(nextValue: string | null) => onChangeFilter('customer_type')(nextValue)}
                value={filters.customer_type}
                items={customerTypeItems}
                label={t('dashboard.filter.customerTypeLabel', 'Type klant')}
                shape='rounded'
                uniformFont
                noShadow
                className={classNames('filter-select', isActive && 'active-filter')}
                itemsClassName='filter-select-items'
            />
        );
    }

    function renderPeriod() {
        const isActive = fieldIsActive(dateFrom) || fieldIsActive(dateTo);
        // No dates → 'Alles' (no restriction), so the selector shows the
        // preset list on open. A null preset WITH dates means a custom range —
        // the selector then opens straight into its custom-range view.
        const selectedPreset = periodPreset ?? (isActive ? null : PeriodLabel.ALL);
        return (
            <BluematePeriodSelector
                onChange={onChangePeriod}
                value={[dateFrom, dateTo]}
                selectedPreset={selectedPreset}
                label={t('dashboard.filter.orderPeriodLabel', 'Periode')}
                selectShape='rounded'
                noShadow
                uniformFont
                max={IDate.now().maxTime()}
                className={classNames('filter-period', isActive && 'active-filter')}
            />
        );
    }

    function renderBody() {
        return (
            <div className='filter-body'>
                <label className='row-label'>{t('dashboard.filter.sectionInvoice', 'Factuur')}</label>
                <div className='row'>
                    <BluemateInput
                        onChange={onChangeFilter('order_id')}
                        value={filters.order_id ?? ''}
                        label={t('dashboard.filter.orderNumber', 'Ordernummer')}
                        onEnter={onSubmitFilters}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.order_id)}
                        active={fieldIsActive(filters.order_id)}
                    />
                    <BluemateInput
                        onChange={onChangeFilter('invoice_num')}
                        value={filters.invoice_num ?? ''}
                        label={t('dashboard.filter.invoiceNumber', 'Factuurnummer')}
                        onEnter={onSubmitFilters}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.invoice_num)}
                        active={fieldIsActive(filters.invoice_num)}
                    />
                </div>
                <div className='row'>
                    {renderSelect('invoice_type', t('dashboard.filter.invoiceKind', 'Soort factuur'), invoiceTypeItems)}
                    {renderSelect('status', t('dashboard.filter.invoiceStatus', 'Status factuur'), statusItems)}
                </div>
                <div className='row'>
                    <BluemateInput
                        onChange={onChangeFilter('reference')}
                        value={filters.reference ?? ''}
                        label={t('dashboard.filter.reference', 'Referentie')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.reference)}
                        active={fieldIsActive(filters.reference)}
                        onEnter={onSubmitFilters}
                    />
                    {renderPeriod()}
                </div>
                <label className='row-label'>{t('dashboard.filter.sectionCustomer', 'Klant')}</label>
                <div className='row'>{renderCustomerType()}</div>
                <div className='row'>
                    <BluemateInput
                        onChange={onChangeFilter('firstname')}
                        value={filters.firstname ?? ''}
                        label={t('dashboard.filter.firstName', 'Voornaam')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.firstname)}
                        active={fieldIsActive(filters.firstname)}
                        onEnter={onSubmitFilters}
                    />
                    <BluemateInput
                        onChange={onChangeFilter('lastname')}
                        value={filters.lastname ?? ''}
                        label={t('dashboard.filter.lastName', 'Achternaam')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.lastname)}
                        active={fieldIsActive(filters.lastname)}
                        onEnter={onSubmitFilters}
                    />
                </div>
                <div className='row'>
                    {renderSelect('country', t('dashboard.filter.country', 'Land'), countryItems, '20rem')}
                    <BluemateInput
                        onChange={onChangeFilter('company')}
                        value={filters.company ?? ''}
                        label={t('dashboard.filter.companyName', 'Bedrijfsnaam')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.company)}
                        active={fieldIsActive(filters.company)}
                        onEnter={onSubmitFilters}
                    />
                </div>
                <div className='row'>
                    <BluemateInput
                        onChange={onChangeFilter('vat_number')}
                        value={filters.vat_number ?? ''}
                        label={t('dashboard.filter.vatNumber', 'BTW nummer')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.vat_number)}
                        active={fieldIsActive(filters.vat_number)}
                        onEnter={onSubmitFilters}
                    />
                    <BluemateInput
                        onChange={onChangeFilter('coc_number')}
                        value={filters.coc_number ?? ''}
                        label={t('dashboard.filter.cocNumber', 'KvK nummer')}
                        shape='rounded'
                        uniformFont
                        labelClassName={getLabelClassName(filters.coc_number)}
                        active={fieldIsActive(filters.coc_number)}
                        onEnter={onSubmitFilters}
                    />
                </div>
                <label className='row-label'>{t('dashboard.filter.sectionOrder', 'Bestelling')}</label>
                <div className='row'>
                    {renderSelect(
                        'order_status',
                        t('dashboard.filter.orderStatusLabel', 'Status bestelling'),
                        orderStatusItems
                    )}
                    <BluemateInput
                        onChange={onChangeFilter('ean')}
                        value={filters.ean ?? ''}
                        label={t('dashboard.filter.eanProduct', 'EAN besteld product')}
                        shape='rounded'
                        uniformFont
                        className={getLabelClassName(filters.ean)}
                        active={fieldIsActive(filters.ean)}
                        onEnter={onSubmitFilters}
                        placeholder='EAN'
                    />
                </div>
            </div>
        );
    }

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={t('dashboard.advancedSearch', 'Uitgebreid zoeken')}
            onConfirm={onSubmitFilters}
            confirmLabel={t('dashboard.filter.search', 'Zoeken')}
            onCancel={onResetFilters}
            cancelLabel={t('dashboard.filter.clearFilters', 'Filters wissen')}
            cancelVariant='tertiary'
            className='invoice-filter-dialog'
            width='50rem'
        >
            {renderBody()}
        </BluemateDialog>
    );
}
